import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Factory } from "lucide-react";
import { motion } from "framer-motion";
import { formatINR, resolveImg } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart, isAtStockLimit } = useCart();
  const [imgLoaded, setImgLoaded] = useState(false);
  const inStock = Number(product.stock ?? 0) > 0;
  const atLimit = isAtStockLimit ? isAtStockLimit(product) : false;

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    await addToCart(product, 1);
  };

  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } } }}
      data-testid="product-card"
      onClick={() => navigate(`/product/${product.id}`)}
      className="group cursor-pointer"
    >
      {/* Image surface — soft beige, borderless */}
      <div className="relative rounded-2xl overflow-hidden bg-[hsl(var(--sand))]">
        <div className="aspect-square">
          <img
            src={resolveImg((product.images && product.images[0]) || "https://images.unsplash.com/photo-1605100804763-247f67b3557e?auto=format&fit=crop&w=800&q=80")}
            alt={product.name}
            loading="lazy"
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgLoaded(true)}
            className={`h-full w-full object-cover transition-[opacity,transform] duration-500 group-hover:scale-105 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          />
        </div>
      </div>

      {/* Details */}
      <div className="pt-3 flex items-end justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm sm:text-[15px] text-foreground/90 leading-snug line-clamp-1">{product.name}</h3>
          <div className="flex items-baseline gap-2 mt-1.5">
            <span data-testid="product-card-price" className="font-mono text-sm sm:text-[15px] font-semibold text-foreground">{formatINR(product.price)}</span>
          </div>
        </div>
        <button
          data-testid={inStock ? "product-card-add-to-cart-button" : "product-card-make-to-order-button"}
          onClick={handleAddToCart}
          disabled={atLimit}
          aria-label={inStock ? "Add to cart — In stock" : "Make to Order"}
          title={inStock ? (atLimit ? "No more available" : "Add to Cart · In Stock") : "Make to Order"}
          className={`shrink-0 h-9 w-9 rounded-full tap-target-44 flex items-center justify-center transition-[opacity,transform,background-color,color] duration-200 active:scale-95 disabled:opacity-40 disabled:pointer-events-none opacity-100 translate-y-0 sm:opacity-0 sm:translate-y-1 sm:group-hover:opacity-100 sm:group-hover:translate-y-0 sm:group-focus-within:opacity-100 ${
            inStock
              ? "text-[hsl(var(--gold-dark))] hover:bg-[hsl(var(--gold-light))] hover:text-[hsl(var(--primary))]"
              : "text-[hsl(var(--gold-dark))] hover:bg-[hsl(var(--gold-light))] hover:text-[hsl(var(--primary))]"
          }`}
        >
          {inStock ? <ShoppingBag strokeWidth={1.75} className="h-[18px] w-[18px]" /> : <Factory strokeWidth={1.75} className="h-[18px] w-[18px]" />}
        </button>
      </div>
    </motion.div>
  );
};
